import {
  Component,
  effect,
  ElementRef,
  HostBinding,
  inject,
  Injector,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { NgxsModule, Store } from '@ngxs/store';
import { firstValueFrom, catchError, of, Subject, Subscription, takeUntil, Observable, merge, map } from 'rxjs';
import { LoadIncident, ResetActiveIncident, UpdateIncident } from '@app/store/app/states/incidents/incidents.actions';
import { RimsPhaseCardComponent } from '@app/pages/incidents/details-page/rims/rims-phase-card/rims-phase-card.component';
import { MenuItemsCollection, SectionMenuComponent } from '@app/components/section-menu/section-menu.component';

// Models
import { RimsPhaseInfo } from '@app/models/rims.model';

// Enums
import { Motivation } from '@app/enums/motivation';

// States
import { IncidentsState } from '@app/store/app/states/incidents/incidents.state';
import { IncidentsService } from '@app/services/incidents/incidents.service';
import { AuthenticationStateModel, AuthState } from '@app/store/auth/auth.state';
import { UserRole } from '@app/store/auth/userRole';
import { ToastrService } from 'ngx-toastr';
import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { ConfirmationModalComponent } from '@app/components/confirmation-modal/confirmation-modal.component';
import { IncidentDetailsPageStore } from './incident-details-page.store';
import { createDate, formatAsDateTimeStringIgnoringTheTime } from '@app/utils/datetime/datetime.utils';
import { ArticleReferencesComponent } from './references/article-references/article-references.component';
import { MediaReferencesComponent } from './references/media-references/media-references.component';
import { MasterDuplicateReferencesComponent } from '@app/pages/incidents/details-page/references/master-duplicate-references/master-duplicate-references.component';
import { AddArticleReferenceModel } from '@app/models/incident-references-edit.model';
import { IncidentModel } from '@app/models/incident.model';
import { ArticleReferenceModel } from '@app/models/article-reference.model';
import { MediaReferenceMetadataModel } from '@app/models/media-reference-metadata.model';
import { ActivatedRoute, Router } from '@angular/router';
import { IncidentPatchModel } from './models/incident-patch.model';
import { IncidentCreateModel } from './models/incident-create.model';
import { Location } from '@angular/common';
import { fileToMediaFileModelAsync } from '@app/utils/incidents/incidents.utils';
import { IncidentStatus } from '@app/enums/incident-status';
import { InfoBannerComponent } from '@app/components/info-banner/info-banner.component';
import { RimsState } from '@app/store/app/states/rims/rims.state';
import { TechnicalStepModel } from '@app/models/technical-step.model';
import { EntityComponent } from './entity/entity.component';
import { ThreatActorComponent } from './threat-actor/threat-actor.component';
import { IncidentInfoComponent } from '@app/pages/incidents/details-page/incident-info/incident-info.component';
import { MergeViewComponent } from './merge-view/merge-view.component';
import { FieldReferenceService } from './field-references.service';
import { AddDuplicateModalComponent } from './add-duplicate-modal/add-duplicate-modal.component';
import { AddDuplicateResultModel } from './merge-view/models/add-duplicate-result.model';

@Component({
  standalone: true,
  selector: 'app-incident-details-page',
  imports: [
    CommonModule,
    NgxsModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatIconModule,
    MatListModule,
    MatMenuModule,
    MatDialogModule,
    RimsPhaseCardComponent,
    SectionMenuComponent,
    IncidentInfoComponent,
    EntityComponent,
    ThreatActorComponent,
    ArticleReferencesComponent,
    MediaReferencesComponent,
    MasterDuplicateReferencesComponent,
    InfoBannerComponent,
    MergeViewComponent,
  ],
  providers: [
    IncidentDetailsPageStore, // Inject here to start a Scoped instance
    FieldReferenceService, // Needs to be injected right next to the store because it uses it
  ],
  templateUrl: './incident-details-page.component.html',
  styleUrl: './incident-details-page.component.scss',
})
export class IncidentDetailsPageComponent implements OnInit, OnDestroy {
  // Used to get the height of the navigation bar to set a CSS variable for the merge view table to use for its height calculation
  @ViewChild('navigationEl') private navigationEl?: ElementRef<HTMLElement>;

  private readonly dialog = inject(MatDialog);
  private destroy$ = new Subject<void>();
  private formGroupSyncSubscription?: Subscription;
  private fieldReferenceSyncSubscription?: Subscription;
  protected rimsPhaseInfos: RimsPhaseInfo[] = [];
  protected isCreateMode = false;
  protected shouldShowValidationSummary = false;
  protected isMergeView = false;

  protected readonly sectionMenuItems: MenuItemsCollection = {
    Details: 'details',
    'Affected Entity': 'affected-entity',
    Rims: 'rims',
    'Threat Actor': 'threat-actor',
    References: 'references',
  };

  public formGroup: FormGroup;

  public user: AuthenticationStateModel | null = null;
  public userRole = UserRole;
  protected readonly IncidentStatus = IncidentStatus;

  @HostBinding('style.--incident-navigation-height.px')
  protected incidentNavigationHeightCssVar = 0;

  constructor(
    private readonly store: Store,
    private readonly incidentsService: IncidentsService,
    private readonly toastr: ToastrService,
    private readonly formBuilder: FormBuilder,
    private readonly router: Router,
    private readonly activatedRoute: ActivatedRoute,
    private readonly location: Location,
    protected readonly incidentsPageStore: IncidentDetailsPageStore,
    private readonly fieldReferenceService: FieldReferenceService,
    private readonly injector: Injector,
  ) {
    this.formGroup = this.buildFormGroup();
    this.connectFormGroupToStore();
    this.connectFieldReferenceSyncToFormGroup();

    effect(() => {
      if (this.incidentsPageStore.incident()) {
        // IncidentDetailsPageComponent: incident changed, updating navigation height CSS variable
        // Wait one task for Angular to render the @if block before reading the DOM
        setTimeout(() => {
          this.incidentNavigationHeightCssVar = this.navigationEl?.nativeElement.offsetHeight ?? 0;
        });
      }
    });
  }

  public async ngOnInit(): Promise<void> {
    this.rimsPhaseInfos = await firstValueFrom(this.store.select(RimsState.getRimsPhaseInfos));

    this.store
      .select(AuthState.getAuthData)
      .pipe(takeUntil(this.destroy$))
      .subscribe((user) => {
        this.user = user;
      });
    this.store
      .select(IncidentsState.getIncident)
      .pipe(takeUntil(this.destroy$))
      .subscribe((incident) => {
        if (incident) {
          this.incidentsPageStore.setIncident(incident);

          if (incident.status === IncidentStatus.Pending) {
            this.loadIncidentAsPending();
          }

          if (incident.isMasterIncident && this.incidentsPageStore.shouldFetchMergeViewData()) {
            this.loadMergeViewDataAsync().then(() => this.loadMergedArticleAndMediaReferences());
          }
        }
      });

    const paths = this.activatedRoute.snapshot.url.map((urlSegment) => urlSegment.path);
    if (paths.includes('create')) {
      await this.initializeComponentAsCreateAsync();
    }
  }

  private async initializeComponentAsCreateAsync(): Promise<void> {
    this.isCreateMode = true;

    const incident = {
      isReviewed: true,
      technicalSteps: [] as TechnicalStepModel[],
    } as IncidentModel;
    await firstValueFrom(this.store.dispatch(new UpdateIncident({ incident })));

    await this.enterEditMode();
  }

  public setView(isMergeView: boolean): void {
    this.isMergeView = isMergeView;
  }

  public getMissingRequiredFieldLabels(): string[] {
    const missingLabels: string[] = [];

    if (this.formGroup.get('incidentTitle')?.hasError('required')) {
      missingLabels.push('Incident Title');
    }

    if (this.formGroup.get('incidentSummary')?.hasError('required')) {
      missingLabels.push('Summary');
    }

    if (this.formGroup.get('incidentAttackPeriod.dateStart')?.hasError('required')) {
      missingLabels.push('Start date');
    }

    return missingLabels;
  }

  private loadIncidentAsPending(): void {
    this.incidentsPageStore.isPending.set(true);
    this.incidentsPageStore.isEditMode.set(true);

    this.formGroup = this.buildFormGroup({
      incidentTitle: ['Unknown', Validators.required],
      incidentSummary: ['Unknown', Validators.required],
      motivation: Motivation.Unknown,
      incidentIsReviewed: '',
      incidentAttackPeriod: this.formBuilder.group({
        dateStart: [null, Validators.required],
        dateEnd: [null],
      }),
    });
    this.connectFormGroupToStore();
    this.connectFieldReferenceSyncToFormGroup();

    this.formGroup.disable();
  }

  public ngOnDestroy(): void {
    this.formGroupSyncSubscription?.unsubscribe();
    this.fieldReferenceSyncSubscription?.unsubscribe();
    this.store.dispatch(new ResetActiveIncident());
    this.destroy$.next();
    this.destroy$.complete();
  }

  public async enterEditMode(): Promise<void> {
    const incident = this.incidentsPageStore.incident();
    if (!incident) {
      return;
    }

    if (!this.isCreateMode) {
      // Validate we are using latest version
      // If not latest version, reload incident
      const versionResult = await firstValueFrom(
        this.incidentsService.checkIncidentVersion(incident.id, incident.version),
      );
      if (versionResult === false) {
        await firstValueFrom(this.store.dispatch(new LoadIncident({ incidentId: incident.id.toString() })));
      }

      // Check if locked and confirm
      if (incident.isLocked) {
        const dialogRef = this.dialog.open<ConfirmationModalComponent, { title: string; content: string }, boolean>(
          ConfirmationModalComponent,
          {
            data: {
              title: 'Incident locked',
              content: `This incident is currently being edited by another administrator since: ${this.incidentsPageStore.incident()!.lockedDateTime?.replace('T', ' ').replace('Z', '')}. \n\n Please confirm to reload the latest version and proceed with editing.`,
            },
          },
        );
        const dialogResult = await firstValueFrom(dialogRef.afterClosed());
        if (dialogResult == false) {
          return;
        }
      }

      // Acquire the lock
      await this.updateIncidentIsLocked(true);
    }

    // Make a deep clone to edit
    // Toggle edit mode
    this.incidentsPageStore.setIncidentEditFromOriginal();
    if (!this.incidentsPageStore.incident()) {
      this.exitEditMode();
      return;
    }

    this.incidentsPageStore.isEditMode.set(true);
    this.updateFormGroup();
    this.shouldShowValidationSummary = false;
  }

  public async exitEditMode(values: { unlockIncident: boolean } = { unlockIncident: true }): Promise<void> {
    // Always return to the default details layout when leaving edit mode.
    this.isMergeView = false;

    if (values.unlockIncident) {
      await this.updateIncidentIsLocked(false);
    }

    this.formGroup.reset();

    if (this.incidentsPageStore.isFieldReferencesEdited) {
      // We need to reload the whole incident in case there was a detachment.
      const incident = await firstValueFrom(this.store.select(IncidentsState.getIncident));
      if (incident) {
        this.incidentsPageStore.setIncident(incident);
        await this.loadMergeViewDataAsync();
        this.loadMergedArticleAndMediaReferences();
      }
    }

    this.incidentsPageStore.isEditMode.set(false);
    this.incidentsPageStore.isFieldReferencesEdited = false;
  }

  protected async onAddDuplicateAsync(): Promise<void> {
    const dialogRef = this.dialog.open(AddDuplicateModalComponent, {
      width: '25vw',
      maxWidth: 'none',
      injector: this.injector,
    });

    const dialogResult: AddDuplicateResultModel | null = await firstValueFrom(dialogRef.afterClosed());
    if (dialogResult) {
      // get the incident model of the added incident
      const addedIncidentModel = await firstValueFrom(
        this.incidentsService.getIncidentDetails(dialogResult.addedIncidentId),
      );

      // add it to the merge view duplicates list
      const updatedDuplicatesCollection = [...(this.incidentsPageStore.mergeViewDuplicates() ?? [])];
      updatedDuplicatesCollection.push(addedIncidentModel);

      // update the store
      this.incidentsPageStore.setIncident(dialogResult.updatedIncident);
      let originalIncident = this.incidentsPageStore.mergeViewOriginal();
      // Reload original from database when a normal incident is become a master
      if (!originalIncident && updatedDuplicatesCollection.length > 0) {
        originalIncident = await firstValueFrom(
          this.incidentsService.getOriginalIncidentDetails(dialogResult.updatedIncident.id),
        );
      }
      this.incidentsPageStore.setMergeViewCacheData(originalIncident!, updatedDuplicatesCollection);

      // update the list of duplicate incident IDs to attach
      this.incidentsPageStore.duplicateIncidentIdsToAttach.update((idsForAttachment) => [
        ...(idsForAttachment ?? []),
        Number(dialogResult.addedIncidentId),
      ]);

      // important to mark as edited since the new state with the attached incident may contain different field references
      this.incidentsPageStore.isFieldReferencesEdited = true;
    }
  }

  protected async toggleIncidentIsDisabledAsync(): Promise<void> {
    const incidentPatchModel: IncidentPatchModel = {
      status:
        this.incidentsPageStore.incident()!.status === IncidentStatus.Active
          ? IncidentStatus.Disabled
          : IncidentStatus.Active,
    };

    const incidentDetailsUpdateResult = await this.updateIncidentDetails(incidentPatchModel);
    if (incidentDetailsUpdateResult === false) {
      // Incident patch request failed and the error was handled with a message. Return without exiting edit mode.
      return;
    }

    this.toastr.success(
      `Successfully ${this.incidentsPageStore.incident()!.status === IncidentStatus.Disabled ? 'deleted' : 'restored'} incident`,
      '',
      {
        positionClass: 'toast-bottom-right',
      },
    );
  }

  protected async navigateBack(): Promise<void> {
    this.location.back();
  }

  protected async createIncidentAsync(): Promise<void> {
    if (this.formGroup.invalid) {
      this.shouldShowValidationSummary = true;
      return;
    }

    const incidentCreateModel = await this.createIncidentCreateModelAsync();

    let observableRequest: Observable<number | null> = this.incidentsService.createIncident(incidentCreateModel);
    observableRequest = this.toErrorHandledObservableRequest(
      observableRequest,
      'Incident create',
      'Creation failed. Please try again.',
    );
    const response = await firstValueFrom(observableRequest);
    if (!response) {
      return;
    }

    await this.router.navigate([`/incidents/${response}`]);
  }

  private async createIncidentCreateModelAsync(): Promise<IncidentCreateModel> {
    const incidentCreateModel = {} as IncidentCreateModel;

    // Form
    Object.keys(this.formGroup.controls).forEach((key) => {
      const control = this.formGroup.get(key);
      if (!control) {
        return;
      }

      switch (key) {
        case 'incidentTitle':
          incidentCreateModel.title = control.value;
          break;
        case 'incidentSummary':
          incidentCreateModel.summary = control.value;
          break;
        case 'motivation':
          incidentCreateModel.motivation = control.value;
          break;
        case 'incidentIsReviewed':
          incidentCreateModel.isReviewed = control.value;
          break;
        case 'incidentAttackPeriod': {
          const controlValue = control.value as {
            dateStart: Date | undefined;
            dateEnd: Date | null | undefined;
          };
          incidentCreateModel.startedAt = formatAsDateTimeStringIgnoringTheTime(controlValue.dateStart);
          incidentCreateModel.endedAt =
            controlValue.dateEnd == null ? null : formatAsDateTimeStringIgnoringTheTime(controlValue.dateEnd);
          break;
        }
      }
    });

    // Store
    incidentCreateModel.entityId = this.incidentsPageStore.incident()!.entity?.id;
    incidentCreateModel.actorId = this.incidentsPageStore.incident()!.actor?.id;
    incidentCreateModel.technicalSteps = this.incidentsPageStore.incident()!.technicalSteps;

    incidentCreateModel.articleReferences =
      this.incidentsPageStore
        .articleReferencesToAdd()
        ?.map((artRef) => ({ title: artRef.title, url: artRef.url }) as AddArticleReferenceModel) ?? [];
    incidentCreateModel.mediaReferences = await fileToMediaFileModelAsync(
      this.incidentsPageStore.mediaReferencesToAdd()?.map((mediaRef) => mediaRef.file) ?? [],
    );

    return incidentCreateModel;
  }

  protected async updateIncident(): Promise<void> {
    if (this.formGroup.invalid || !this.incidentsPageStore.incident()) {
      this.shouldShowValidationSummary = true;
      return;
    }

    const incidentPatchModel = this.createIncidentPatchModel();
    const incidentDetailsUpdateResult = await this.updateIncidentDetails(incidentPatchModel);
    if (incidentDetailsUpdateResult === false) {
      // Incident patch request failed and the error was handled with a message. Return without exiting edit mode.
      return;
    }

    const referencesUpdateResult = await this.updateIncidentReferences();
    if (referencesUpdateResult === false) {
      // References update request failed and the error was handled with a message. Return without exiting edit mode.
      return;
    }

    this.toastr.success('Successfully updated.', 'Incident update', {
      positionClass: 'toast-bottom-right',
    });

    // Incident does not need unlocking when an incident patch request was sent. The patch request updated the incident and unlocks it before the response.
    await this.exitEditMode({ unlockIncident: !incidentDetailsUpdateResult });
  }

  protected updateFormGroup(markAsTouched = false): void {
    const {
      title: incidentTitle = '',
      summary: incidentSummary = '',
      motivation: motivation = Motivation.Unknown,
      isReviewed: incidentIsReviewed = false,
      startedAt: dateStart = '',
      endedAt: dateEnd = '',
    } = this.incidentsPageStore.incident()!;

    this.formGroup.setValue(
      {
        incidentTitle,
        incidentSummary,
        motivation,
        incidentIsReviewed,
        incidentAttackPeriod: {
          dateStart: createDate(dateStart),
          dateEnd: createDate(dateEnd),
        },
      },
      { emitEvent: false },
    );

    if (markAsTouched) {
      this.formGroup.markAllAsTouched();
    }
  }

  private buildFormGroup(
    overrides?: Partial<Record<'incidentTitle' | 'incidentSummary' | 'motivation' | 'incidentIsReviewed', unknown>> & {
      incidentAttackPeriod?: FormGroup;
    },
  ): FormGroup {
    return this.formBuilder.group({
      incidentTitle: overrides?.incidentTitle ?? ['', Validators.required],
      incidentSummary: overrides?.incidentSummary ?? ['', Validators.required],
      motivation: overrides?.motivation ?? Motivation.Unknown,
      incidentIsReviewed: overrides?.incidentIsReviewed ?? '',
      incidentAttackPeriod:
        overrides?.incidentAttackPeriod ??
        this.formBuilder.group({
          dateStart: [null, Validators.required],
          dateEnd: [null],
        }),
    });
  }

  private connectFormGroupToStore(): void {
    this.formGroupSyncSubscription?.unsubscribe();
    this.formGroupSyncSubscription = this.formGroup.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.syncIncidentEditFromFormGroup();
    });
  }

  // Marks a manually edited field's reference as unchecked, overriding any previously merged-in source.
  private connectFieldReferenceSyncToFormGroup(): void {
    this.fieldReferenceSyncSubscription?.unsubscribe();

    const attackPeriodGroup = this.formGroup.get('incidentAttackPeriod') as FormGroup;

    this.fieldReferenceSyncSubscription = merge(
      this.formGroup.get('incidentTitle')!.valueChanges.pipe(map(() => 'Title')),
      this.formGroup.get('incidentSummary')!.valueChanges.pipe(map(() => 'Summary')),
      this.formGroup.get('motivation')!.valueChanges.pipe(map(() => 'Motivation')),
      attackPeriodGroup.get('dateStart')!.valueChanges.pipe(map(() => 'StartedAt')),
      attackPeriodGroup.get('dateEnd')!.valueChanges.pipe(map(() => 'EndedAt')),
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe((fieldName) => this.uncheckIncidentFieldReferenceForFormField(fieldName));
  }

  private uncheckIncidentFieldReferenceForFormField(name: string): void {
    if (!this.incidentsPageStore.isEditMode()) {
      return;
    }

    this.fieldReferenceService.uncheckIncidentFieldReference(name);
  }

  private syncIncidentEditFromFormGroup(): void {
    if (!this.incidentsPageStore.isEditMode() || !this.incidentsPageStore.incident()) {
      return;
    }

    const attackPeriod = this.formGroup.get('incidentAttackPeriod')?.value as
      | {
          dateStart: Date | null | undefined;
          dateEnd: Date | null | undefined;
        }
      | undefined;

    this.incidentsPageStore.updateIncidentEditDetails({
      title: this.formGroup.get('incidentTitle')?.value ?? '',
      summary: this.formGroup.get('incidentSummary')?.value ?? '',
      motivation: this.formGroup.get('motivation')?.value ?? Motivation.Unknown,
      isReviewed: this.formGroup.get('incidentIsReviewed')?.value ?? false,
      startedAt: formatAsDateTimeStringIgnoringTheTime(attackPeriod?.dateStart) || null,
      endedAt: attackPeriod?.dateEnd == null ? null : formatAsDateTimeStringIgnoringTheTime(attackPeriod.dateEnd),
    });
  }

  /**
   *
   * @returns
   * true if we have a success,
   * false if there was an error,
   * null if there was nothing to update.
   */
  private async updateIncidentDetails(incidentPatchModel: IncidentPatchModel): Promise<boolean | null> {
    // No data for update collected in the dictionary. Skip the request.
    if (Object.keys(incidentPatchModel).length === 0) {
      return null;
    }

    // Unlock the incident upon update
    incidentPatchModel.isLocked = false;

    let observableRequest: Observable<IncidentModel | null> = this.incidentsService.patchIncidentDetails(
      this.incidentsPageStore.incident()!.id,
      this.incidentsPageStore.incident()!.version,
      incidentPatchModel,
    );
    observableRequest = this.toErrorHandledObservableRequest(
      observableRequest,
      'Incident update',
      'Update failed. Please try again.',
    );
    const response = await firstValueFrom(observableRequest);
    if (!response) {
      return false;
    }

    await firstValueFrom(this.store.dispatch(new UpdateIncident({ incident: response })));
    return true;
  }

  private createIncidentPatchModel(): IncidentPatchModel {
    const incidentPatchModel: IncidentPatchModel = {};

    // Form
    Object.keys(this.formGroup.controls).forEach((key) => {
      const control = this.formGroup.get(key);
      if (!control || !control.touched) {
        return;
      }

      switch (key) {
        case 'incidentTitle':
          incidentPatchModel.title = control.value;
          break;
        case 'incidentSummary':
          incidentPatchModel.summary = control.value;
          break;
        case 'motivation':
          incidentPatchModel.motivation = control.value;
          break;
        case 'incidentIsReviewed':
          incidentPatchModel.isReviewed = control.value;
          break;
        case 'incidentAttackPeriod': {
          const controlValue = control.value as {
            dateStart: Date | undefined;
            dateEnd: Date | null | undefined;
          };
          incidentPatchModel.startedAt = formatAsDateTimeStringIgnoringTheTime(controlValue.dateStart);
          incidentPatchModel.endedAt =
            controlValue.dateEnd == null ? null : formatAsDateTimeStringIgnoringTheTime(controlValue.dateEnd);
          break;
        }
      }
    });

    // Store
    if (!this.incidentsPageStore.isIncidentEntityModified()) {
      incidentPatchModel.entityId = this.incidentsPageStore.incident()!.entity?.id ?? null;
    }
    if (!this.incidentsPageStore.isIncidentActorModified()) {
      incidentPatchModel.actorId = this.incidentsPageStore.incident()!.actor?.id ?? null;
    }
    if (this.incidentsPageStore.isRimsEdited) {
      incidentPatchModel.technicalSteps = this.incidentsPageStore.incident()!.technicalSteps;
    }
    if (this.incidentsPageStore.isFieldReferencesEdited) {
      incidentPatchModel.fieldReferences = this.incidentsPageStore.incident()!.fieldReferences;
    }
    if (this.incidentsPageStore.duplicateIncidentIdsToDetach() != null) {
      incidentPatchModel.duplicateIncidentIdsToDetach = this.incidentsPageStore.duplicateIncidentIdsToDetach()!;
    }
    if (this.incidentsPageStore.duplicateIncidentIdsToAttach() != null) {
      incidentPatchModel.duplicateIncidentIdsToAttach = this.incidentsPageStore.duplicateIncidentIdsToAttach()!;
    }

    return incidentPatchModel;
  }

  /**
   *
   * @returns Returns true if we have a success. Returns false if there was an error. Returns null if there was nothing to update.
   */
  private async updateIncidentReferences(): Promise<boolean | null> {
    const articleRefsToAdd =
      this.incidentsPageStore
        .articleReferencesToAdd()
        ?.map((artRef) => ({ title: artRef.title, url: artRef.url }) as AddArticleReferenceModel) ?? null;
    const articleRefsToUnlink = this.incidentsPageStore.articleReferenceIdsToDelete();
    const mediaRefsToAdd = this.incidentsPageStore.mediaReferencesToAdd()?.map((mediaRef) => mediaRef.file) ?? null;
    const mediaRefsToUnlink = this.incidentsPageStore.mediaReferenceIdsToDelete();

    // At least one collection should be populated in order to send a request
    if (
      (!articleRefsToAdd || articleRefsToAdd.length === 0) &&
      (!articleRefsToUnlink || articleRefsToUnlink.length === 0) &&
      (!mediaRefsToAdd || mediaRefsToAdd.length === 0) &&
      (!mediaRefsToUnlink || mediaRefsToUnlink.length === 0)
    ) {
      return null;
    }

    let observableRequest: Observable<IncidentModel | null> = await this.incidentsService.editIncidentReferences(
      this.incidentsPageStore.incident()!.id,
      {
        articleReferencesToAdd: articleRefsToAdd,
        articleReferenceIdsToUnlink: articleRefsToUnlink,
        mediaReferencesToAdd: mediaRefsToAdd,
        mediaReferenceIdsToUnlink: mediaRefsToUnlink,
      },
    );

    observableRequest = this.toErrorHandledObservableRequest(
      observableRequest,
      'Incident references update',
      'Incident references update failed. Please try again.',
    );

    const response = await firstValueFrom(observableRequest);
    if (!response) {
      return false;
    }

    await firstValueFrom(this.store.dispatch(new UpdateIncident({ incident: response })));
    return true;
  }

  private async updateIncidentIsLocked(isLocked: boolean): Promise<void> {
    const incidentPatchModel: IncidentPatchModel = { isLocked: isLocked };

    const updatedIncidentModel = await firstValueFrom(
      this.incidentsService
        .patchIncidentDetails(
          this.incidentsPageStore.incident()!.id,
          this.incidentsPageStore.incident()!.version,
          incidentPatchModel,
        )
        .pipe(
          catchError((error) => {
            if (error instanceof HttpErrorResponse && error.status === HttpStatusCode.Conflict) {
              this.handleConflictError();
            }

            return of(null);
          }),
        ),
    );

    if (updatedIncidentModel) {
      await firstValueFrom(this.store.dispatch(new UpdateIncident({ incident: updatedIncidentModel })));
    }
  }

  private handleConflictError(): void {
    this.toastr.error('Server encountered a conflict. Please reload the Incident.', 'Incident update.', {
      positionClass: 'toast-bottom-right',
    });
  }

  private toErrorHandledObservableRequest<T>(
    observableRequest: Observable<T>,
    errorTitle: string,
    errorMessage: string,
  ): Observable<T | null> {
    return observableRequest.pipe(
      catchError((error) => {
        if (error instanceof HttpErrorResponse) {
          if (error.status === HttpStatusCode.Conflict) {
            this.handleConflictError();
          } else {
            this.toastr.error(errorMessage, errorTitle, {
              positionClass: 'toast-bottom-right',
            });
          }
        }

        return of(null);
      }),
    );
  }

  // Execute when the incident() is loaded in the store from the ngOnInit of this component
  private async loadMergeViewDataAsync(): Promise<void> {
    const mergedIncident = this.incidentsPageStore.incident();

    if (!mergedIncident) {
      return;
    }

    this.incidentsPageStore.clearMergeViewCache();

    try {
      const originalIncident = await firstValueFrom(
        this.incidentsService.getOriginalIncidentDetails(mergedIncident.id),
      );
      const duplicateIncidentIds = this.normalizeDuplicateIncidentIds(mergedIncident.incidentDuplicateIds);
      const duplicateIncidents: IncidentModel[] = [];

      for (let start = 0; start < duplicateIncidentIds.length; start += 10) {
        const batchIncidentIds = duplicateIncidentIds.slice(start, start + 10);

        try {
          const batchResult = await firstValueFrom(this.incidentsService.getBatchIncidentsByIds(batchIncidentIds));
          duplicateIncidents.push(...batchResult.incidents);
        } catch {
          this.incidentsPageStore.clearMergeViewCache();
          return;
        }
      }

      // Order duplicates by CreatedAt (newest first).
      const orderedDuplicateIncidents = [...duplicateIncidents].sort((left, right) =>
        this.compareByCreatedAt(left, right),
      );

      this.incidentsPageStore.setMergeViewCacheData(originalIncident, orderedDuplicateIncidents);
    } catch {
      this.incidentsPageStore.clearMergeViewCache();
    }
  }

  private normalizeDuplicateIncidentIds(incidentDuplicateIds: number[] | null | undefined): number[] {
    return (incidentDuplicateIds ?? []).filter((incidentId) => Number.isInteger(incidentId) && incidentId > 0);
  }

  private compareByCreatedAt(left: IncidentModel, right: IncidentModel): number {
    const leftCreatedAt = createDate(left.createdAt)?.getTime() ?? null;
    const rightCreatedAt = createDate(right.createdAt)?.getTime() ?? null;

    if (leftCreatedAt === rightCreatedAt) {
      return left.id - right.id;
    }
    if (leftCreatedAt === null) {
      return 1;
    }
    if (rightCreatedAt === null) {
      return -1;
    }

    return rightCreatedAt - leftCreatedAt;
  }

  private loadMergedArticleAndMediaReferences(): void {
    this.incidentsPageStore.articleReferencesToMerge.set([]);
    this.incidentsPageStore.mediaReferencesToMerge.set([]);

    const mergedIncident = this.incidentsPageStore.incident();
    const duplicateIncidents = this.incidentsPageStore.mergeViewDuplicates();
    const originalIncident = this.incidentsPageStore.mergeViewOriginal();

    if (!mergedIncident || !duplicateIncidents || !originalIncident) {
      return;
    }

    // Article References
    const allArticleReferencesNotPresentInTheMergedIncident: ArticleReferenceModel[] = [
      ...duplicateIncidents.flatMap((d) => d.articleReferences ?? []),
      ...(originalIncident.articleReferences ?? []),
    ].filter(
      (articleRef) =>
        !mergedIncident.articleReferences?.some((mergedArticleRef) => mergedArticleRef.id === articleRef.id),
    );

    const sourcedArticleReferenceIds = this.fieldReferenceService
      .takeAllArticleReferenceFieldReferences()
      ?.filter((fieldRef) => fieldRef.state === 'Sourced')
      .map((fr) => fr.sourceId);

    const articleRefsToAddAsMerged = allArticleReferencesNotPresentInTheMergedIncident.filter((articleRef) =>
      sourcedArticleReferenceIds?.some((sourceArticleId) => articleRef.id === sourceArticleId),
    );
    this.fieldReferenceService.addArticleToArticleReferencesToMergeCollection(articleRefsToAddAsMerged);

    // Media References
    const allMediaReferencesNotPresentInTheMergedIncident: MediaReferenceMetadataModel[] = [
      ...duplicateIncidents.flatMap((d) => d.mediaReferences ?? []),
      ...(originalIncident.mediaReferences ?? []),
    ].filter(
      (mediaRef) => !mergedIncident.mediaReferences?.some((mergedMediaRef) => mergedMediaRef.id === mediaRef.id),
    );

    const sourcedMediaReferenceIds = this.fieldReferenceService
      .takeAllMediaReferenceFieldReferences()
      ?.filter((fieldRef) => fieldRef.state === 'Sourced')
      .map((fr) => fr.sourceId);

    const mediaRefsToAddAsMerged = allMediaReferencesNotPresentInTheMergedIncident.filter((mediaRef) =>
      sourcedMediaReferenceIds?.some((sourceMediaId) => mediaRef.id === sourceMediaId),
    );
    this.fieldReferenceService.addMediaToMediaReferencesToMergeCollection(mediaRefsToAddAsMerged);
  }
}
